import React, { useMemo } from "react";
import {
  ChevronLeft,
  Lock,
  MoreHorizontal,
  PlusSquare,
  RotateCw,
  Share,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getInstallGuideCopy } from "@/lib/pwaInstallI18n";

function Highlight({ active, children, className }) {
  return (
    <div
      className={cn(
        "rounded-xl transition-all",
        active && "relative z-10 ring-[2.5px] ring-red-500 ring-offset-2 ring-offset-white",
        className
      )}
    >
      {children}
    </div>
  );
}

function StepBadge({ n, title, hint }) {
  return (
    <div className="mb-2 flex items-start gap-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
        {n}
      </span>
      <div>
        <p className="text-[15px] font-bold text-gray-900">{title}</p>
        <p className="text-xs leading-relaxed text-gray-500">{hint}</p>
      </div>
    </div>
  );
}

function BottomBarMock({ highlightMenu }) {
  return (
    <div dir="ltr" className="rounded-2xl bg-[#ececee] px-2 py-2">
      <div className="flex items-center gap-1">
        <ChevronLeft className="h-5 w-5 shrink-0 text-[#007AFF]" strokeWidth={2.5} />
        <div className="h-[18px] w-[18px] shrink-0 rounded-[3px] border-2 border-[#007AFF]" />
        <div className="flex min-w-0 flex-1 items-center justify-center gap-1 rounded-lg bg-white/80 px-2 py-1">
          <Lock className="h-3 w-3 text-gray-400" />
          <span className="truncate text-xs font-medium">reshet.app</span>
        </div>
        <RotateCw className="h-4 w-4 shrink-0 text-[#007AFF]" />
        <Highlight active={highlightMenu}>
          <div className="flex h-8 w-8 items-center justify-center">
            <MoreHorizontal className="h-5 w-5 text-[#007AFF]" strokeWidth={2.5} />
          </div>
        </Highlight>
      </div>
    </div>
  );
}

function PageMenuMock({ t, highlightShare }) {
  const rows = [
    { key: "share", icon: Share, highlight: highlightShare },
    { key: "addToBookmarks", highlight: false },
    { key: "addBookmarkTo", highlight: false },
    { key: "newTab", highlight: false },
    { key: "newPrivateTab", highlight: false },
  ];

  return (
    <div dir="rtl" className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
      {rows.map(({ key, icon: Icon, highlight }) => (
        <Highlight key={key} active={highlight}>
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0">
            {Icon ? <Icon className="h-5 w-5 shrink-0 text-[#007AFF]" strokeWidth={2} /> : null}
            <span className="text-[15px] text-gray-900">{t.ui[key]}</span>
          </div>
        </Highlight>
      ))}
    </div>
  );
}

function ShareSheetMock({ t, highlightMore, highlightAdd }) {
  const actionRow = [
    { key: "copy", highlight: false },
    { key: "addToBookmarks", highlight: false },
    { key: "addToReadingList", highlight: false },
    { key: "more", highlight: highlightMore },
  ];

  const extendedRows = [
    { key: "addToFavourites", highlight: false },
    { key: "findOnPage", highlight: false },
    { key: "addToHomeScreen", highlight: highlightAdd, icon: PlusSquare },
  ];

  return (
    <div dir="rtl" className="overflow-hidden rounded-2xl border border-gray-200 bg-[#f2f2f7]">
      <div className="flex items-center gap-2.5 border-b border-gray-200 bg-white px-3 py-2">
        <img src="/pwa-192x192.png" alt="" className="h-9 w-9 rounded-lg" />
        <div>
          <p className="text-sm font-semibold text-gray-900">{t.siteName}</p>
          <p className="text-[11px] text-gray-500">reshet.app</p>
        </div>
      </div>

      {!highlightAdd && (
        <>
          <div className="flex gap-2 overflow-x-auto px-2 py-2">
            {[t.ui.freeform, "Chromium", t.ui.chrome, t.ui.more].map((name) => (
              <div key={name} className="flex w-12 shrink-0 flex-col items-center gap-0.5">
                <div className="h-10 w-10 rounded-xl bg-white shadow-sm" />
                <span className="w-full truncate text-center text-[9px] text-gray-500">{name}</span>
              </div>
            ))}
          </div>
          <div className="mx-2 rounded-xl bg-white">
            {actionRow.map(({ key, highlight }) => (
              <Highlight key={key} active={highlight}>
                <div className="border-b border-gray-100 px-3 py-2.5 text-center text-[13px] text-gray-800 last:border-0">
                  {t.ui[key]}
                </div>
              </Highlight>
            ))}
          </div>
        </>
      )}

      {highlightAdd && (
        <div className="mx-2 my-2 rounded-xl bg-white">
          {extendedRows.map(({ key, highlight, icon: Icon }) => (
            <Highlight key={key} active={highlight}>
              <div className="flex items-center gap-2.5 border-b border-gray-100 px-3 py-3 last:border-0">
                {Icon ? <Icon className="h-5 w-5 text-gray-700" /> : null}
                <span className="text-[15px] font-medium text-gray-900">{t.ui[key]}</span>
              </div>
            </Highlight>
          ))}
          <p className="py-1.5 text-center text-[10px] text-gray-400">{t.scrollHint}</p>
        </div>
      )}
    </div>
  );
}

function IosVisualGuide({ t }) {
  return (
    <div className="space-y-5">
      <div>
        <StepBadge n={1} title={t.steps.menu.title} hint={t.steps.menu.hint} />
        <BottomBarMock highlightMenu />
      </div>

      <div>
        <StepBadge n={2} title={t.steps.share.title} hint={t.steps.share.hint} />
        <PageMenuMock t={t} highlightShare />
      </div>

      <div>
        <StepBadge n={3} title={t.steps.more.title} hint={t.steps.more.hint} />
        <ShareSheetMock t={t} highlightMore highlightAdd={false} />
      </div>

      <div>
        <StepBadge n={4} title={t.steps.add.title} hint={t.steps.add.hint} />
        <ShareSheetMock t={t} highlightMore={false} highlightAdd />
      </div>
    </div>
  );
}

function GenericGuide({ steps }) {
  return (
    <ol className="space-y-3">
      {steps.map((step, i) => (
        <li key={step} className="flex gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
            {i + 1}
          </span>
          <p className="pt-1 text-sm text-gray-700">{step}</p>
        </li>
      ))}
    </ol>
  );
}

export default function PwaInstallGuideDialog({ open, onOpenChange, instructions }) {
  const t = useMemo(() => getInstallGuideCopy(), [open]);

  if (!instructions) return null;

  const showIosVisual =
    instructions.variant === "ios-safari" || instructions.variant === "ios-chrome";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-sm gap-0 overflow-hidden p-0 sm:rounded-2xl">
        <div className="border-b border-gray-100 px-5 pb-4 pt-5 text-center">
          <img
            src="/pwa-192x192.png"
            alt=""
            className="mx-auto mb-2.5 h-12 w-12 rounded-2xl shadow-sm ring-1 ring-black/5"
          />
          <DialogHeader className="space-y-1 text-center sm:text-center">
            <DialogTitle className="text-lg font-bold">{showIosVisual ? t.title : instructions.title}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {instructions.inAppBrowser ? t.openInSafari : t.subtitle}
            </DialogDescription>
          </DialogHeader>
          {instructions.devPreview ? (
            <p className="mt-1.5 text-[10px] text-gray-400">{t.devPreview}</p>
          ) : null}
        </div>

        <div className="max-h-[62dvh] overflow-y-auto px-4 py-4">
          {instructions.inAppBrowser ? (
            <p className="mb-4 rounded-xl bg-blue-50 py-2.5 text-center text-sm text-blue-900">
              {t.openInSafariAction}
            </p>
          ) : null}

          {showIosVisual ? (
            <IosVisualGuide t={t} />
          ) : (
            <GenericGuide steps={instructions.steps || []} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
